import { useState } from 'react';
import { Check, Zap, Crown, Rocket, User, ArrowRight, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { PRICING_TIERS, formatPrice, calculateTierPrice } from '../utils/pricingConfig';

// Export for backwards compatibility with components using PLAN_DETAILS
export const PLAN_DETAILS = Object.values(PRICING_TIERS).map(tier => ({
  plan: tier.id,
  displayName: tier.displayName,
  price: tier.basePrice / 100, // Convert cents to dollars
  features: tier.features,
}));

interface SubscriptionPlansProps {
  currentPlan?: string;
  currentUserCount?: number;
  onSelectPlan?: (planId: string, priceId: string) => void;
  showCurrentPlanOnly?: boolean;
}

const getPlanIcon = (planId: string) => {
  switch (planId) {
    case 'solo':
      return <User className="h-6 w-6" />;
    case 'starter':
      return <Zap className="h-6 w-6" />;
    case 'premium':
      return <Rocket className="h-6 w-6" />;
    case 'enterprise':
      return <Crown className="h-6 w-6" />;
    default:
      return <Users className="h-6 w-6" />;
  }
};

const getPlanColor = (planId: string) => {
  switch (planId) {
    case 'solo':
      return 'from-slate-500 to-slate-600';
    case 'starter':
      return 'from-blue-500 to-blue-600';
    case 'premium':
      return 'from-purple-500 to-purple-600';
    case 'enterprise':
      return 'from-amber-500 to-amber-600';
    default:
      return 'from-gray-500 to-gray-600';
  }
};

export function SubscriptionPlans({ 
  currentPlan = 'free', 
  currentUserCount = 1,
  onSelectPlan, 
  showCurrentPlanOnly = false 
}: SubscriptionPlansProps) {
  const [selectedUsers, setSelectedUsers] = useState<Record<string, number>>({
    solo: 1,
    starter: 10,
    premium: 25,
    enterprise: 100,
  });

  const plansToShow = showCurrentPlanOnly
    ? Object.entries(PRICING_TIERS).filter(([id]) => id === currentPlan)
    : Object.entries(PRICING_TIERS).filter(([id]) => id !== 'free');

  const handleUserCountChange = (planId: string, count: number) => {
    const tier = PRICING_TIERS[planId];
    if (!tier) return;

    // Enforce max users for solo tier
    if (tier.maxUsers !== undefined && count > tier.maxUsers) {
      count = tier.maxUsers;
    }

    setSelectedUsers(prev => ({ ...prev, [planId]: count }));
  };

  const getButtonText = (planId: string) => {
    if (currentPlan === planId) {
      return 'Current Plan';
    }
    if (currentPlan === 'free') {
      return 'Start Free Trial';
    }
    const currentIndex = ['free', 'solo', 'starter', 'premium', 'enterprise'].indexOf(currentPlan);
    const planIndex = ['free', 'solo', 'starter', 'premium', 'enterprise'].indexOf(planId);
    if (planIndex > currentIndex) {
      return 'Upgrade';
    }
    return 'Downgrade';
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {plansToShow.map(([planId, tier]) => {
        const userCount = selectedUsers[planId] || tier.includedUsers;
        const totalPrice = calculateTierPrice(planId, userCount);
        const overageUsers = Math.max(0, userCount - tier.includedUsers);
        const isCurrentPlan = currentPlan === planId;

        return (
          <Card 
            key={planId} 
            className={`relative overflow-hidden ${tier.popular ? 'ring-2 ring-purple-500' : ''} ${isCurrentPlan ? 'ring-2 ring-blue-500' : ''}`}
          >
            {tier.popular && (
              <div className="absolute top-0 right-0 bg-purple-500 text-white px-3 py-1 text-xs rounded-bl-lg">
                Most Popular
              </div>
            )}
            {isCurrentPlan && (
              <div className="absolute top-0 left-0 bg-blue-500 text-white px-3 py-1 text-xs rounded-br-lg">
                Active
              </div>
            )}

            <CardHeader className="pb-4">
              <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${getPlanColor(planId)} flex items-center justify-center mb-4 text-white`}>
                {getPlanIcon(planId)}
              </div>
              <CardTitle className="text-2xl">{tier.displayName}</CardTitle>
              <CardDescription>
                <div className="mt-4 mb-2">
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-foreground">
                      {formatPrice(totalPrice)}
                    </span>
                    <span className="text-muted-foreground ml-2">/month</span>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Includes {tier.includedUsers} {tier.includedUsers === 1 ? 'user' : 'users'}
                  </div>
                  {tier.overagePrice > 0 && (
                    <div className="text-xs text-muted-foreground mt-1">
                      +{formatPrice(tier.overagePrice)}/additional user
                    </div>
                  )}
                </div>
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4 pb-6">
              {/* User Count Selector (only for tiers that allow overages) */}
              {tier.overagePrice > 0 && !showCurrentPlanOnly && (
                <div className="bg-muted/50 p-3 rounded-lg">
                  <label className="text-sm font-medium mb-2 block">
                    Number of users
                  </label>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUserCountChange(planId, userCount - 1)}
                      disabled={userCount <= tier.includedUsers}
                    >
                      -
                    </Button>
                    <input
                      type="number"
                      min={tier.includedUsers}
                      max={tier.maxUsers}
                      value={userCount}
                      onChange={(e) => handleUserCountChange(planId, parseInt(e.target.value) || tier.includedUsers)}
                      className="w-20 text-center border rounded px-2 py-1"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUserCountChange(planId, userCount + 1)}
                    >
                      +
                    </Button>
                  </div>
                  {overageUsers > 0 && (
                    <div className="text-xs text-muted-foreground mt-2">
                      Base: {formatPrice(tier.basePrice)} + {overageUsers} overage {overageUsers === 1 ? 'user' : 'users'} ({formatPrice(overageUsers * tier.overagePrice)})
                    </div>
                  )}
                </div>
              )}

              {/* Features List */}
              <ul className="space-y-2">
                {tier.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>

            <CardFooter>
              <Button
                className="w-full"
                variant={isCurrentPlan ? 'outline' : tier.popular ? 'default' : 'secondary'}
                disabled={isCurrentPlan || !onSelectPlan}
                onClick={() => onSelectPlan?.(planId, '')}
              >
                {getButtonText(planId)}
                {!isCurrentPlan && <ArrowRight className="h-4 w-4 ml-2" />}
              </Button>
            </CardFooter>
          </Card>
        );
      })}
    </div>
  );
}